import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService as api } from '../services/api';
import { toast } from 'react-toastify';
import { downloadAutenticato } from '../utils/downloadFile';
import { 
  UserIcon, 
  BuildingOfficeIcon, 
  IdentificationIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  BanknotesIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  CalendarIcon,
  ClockIcon,
  ChevronRightIcon,
  PencilIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [documentiPratiche, setDocumentiPratiche] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [creatingPratica, setCreatingPratica] = useState(false);

  useEffect(() => {
    // Controlla se l'utente è admin
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      setIsAdmin(user.role === 'admin');
    }
    
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      setLoading(true);
      const data = await api.getClient(id);
      setClient(data);
    } catch (err) {
      console.error('Errore nel caricamento del cliente:', err);
      setError(err.error || 'Errore nel caricamento del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await api.deleteClient(id);
      navigate('/nlt/clienti');
    } catch (err) {
      console.error('Errore nella cancellazione del cliente:', err);
      setError(err.error || 'Errore nella cancellazione del cliente');
      setLoading(false);
    }
  };

  const handleCreaPratica = async () => {
    try {
      setCreatingPratica(true);
      
      const response = await fetch('/api/pratica', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cliente_id: parseInt(id),
          stato_pratica: 'pratica_avviata',
          note: 'Pratica creata dal dettaglio cliente'
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Pratica creata con successo!');
        
        // Naviga alla pagina della pratica appena creata
        navigate(`/nlt/pratiche/${data.pratica.id}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore nella creazione della pratica');
      }
    } catch (err) {
      console.error('Errore nella creazione della pratica:', err);
      toast.error(err.message || 'Errore nella creazione della pratica');
    } finally {
      setCreatingPratica(false);
    }
  };

  const loadDocumentiPratiche = async () => {
    if (activeTab === 'documents' && documentiPratiche.length === 0) {
      try {
        setLoadingDocuments(true);
        // Carica le pratiche del cliente con i relativi documenti
        const response = await fetch(`/api/pratica/cliente/${id}/documenti`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDocumentiPratiche(data.documenti || []);
        } else {
          console.error('Errore nel caricamento dei documenti delle pratiche');
          setDocumentiPratiche([]);
        }
      } catch (err) {
        console.error('Errore nel caricamento dei documenti delle pratiche:', err);
        setDocumentiPratiche([]);
      } finally {
        setLoadingDocuments(false);
      }
    }
  };
  
  useEffect(() => {
    loadDocumentiPratiche();
  }, [activeTab]);
  
  const createQuotation = async () => {
    try {
      const response = await api.createQuotation(id, {
        importo: 0,
        durata: 36,
        note: 'Preventivo generato automaticamente'
      });
      
      // Qui potresti reindirizzare alla pagina di modifica del preventivo
      toast.success(`Preventivo creato con successo: ${response.quotation.codice || ''}`);
    } catch (err) {
      console.error('Errore nella creazione del preventivo:', err);
      toast.error('Errore nella creazione del preventivo');
    }
  };

  // Gestione dei tabs
  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return renderClientInfo();
      case 'documents':
        return renderDocumentsList();
      default:
        return renderClientInfo();
    }
  };
  
  const renderClientInfo = () => {
    if (!client) return null;
    
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Informazioni Generali</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2 text-gray-700">Dati Anagrafici</h4>
            <div className="space-y-2">
              {client.tipo_cliente === 'privato' ? (
                <>
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm">{client.nome} {client.cognome}</span>
                  </div>
                  <div className="text-sm text-gray-600 ml-7">Codice Fiscale: {client.codice_fiscale || 'Non specificato'}</div>
                </>
              ) : (
                <>
                  <div className="flex items-center">
                    <UserGroupIcon className="h-5 w-5 text-gray-500 mr-2" />
                    <span className="text-sm">{client.ragione_sociale}</span>
                  </div>
                  <div className="text-sm text-gray-600 ml-7">P.IVA: {client.partita_iva || 'Non specificata'}</div>
                  <div className="text-sm text-gray-600 ml-7">Referente: {client.referente || 'Non specificato'}</div>
                </>
              )}
            </div>
            
            <h4 className="font-medium mt-4 mb-2 text-gray-700">Contatti</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-sm">{client.telefono || 'Non specificato'}</span>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-sm">{client.email || 'Non specificato'}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2 text-gray-700">Indirizzo</h4>
            <div className="space-y-2">
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                <div>
                  <div className="text-sm">{client.indirizzo || 'Non specificato'}</div>
                  <div className="text-sm">{client.cap || ''} {client.citta || ''} ({client.provincia || ''})</div>
                </div>
              </div>
            </div>
            
            <h4 className="font-medium mt-4 mb-2 text-gray-700">Informazioni Aggiuntive</h4>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-medium">Stato: </span>
                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                  client.stato === 'attivo' ? 'bg-green-100 text-green-800' : 
                  client.stato === 'prospect' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-blue-100 text-blue-800'
                }`}>
                  {client.stato || 'Prospect'}
                </span>
              </div>
              <div className="text-sm">
                <span className="font-medium">IBAN: </span>
                {client.iban || 'Non specificato'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const renderDocumentsList = () => {
    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 sm:p-6 border-b">
          <h3 className="text-lg font-semibold">Documenti delle Pratiche</h3>
          <p className="text-sm text-gray-500 mt-1">Documenti utilizzati nelle pratiche del cliente</p>
        </div>
        
        {loadingDocuments ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Caricamento documenti...</p>
          </div>
        ) : documentiPratiche.length === 0 ? (
          <div className="p-6 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-2 text-gray-600">Nessun documento associato alle pratiche</p>
            <p className="text-sm text-gray-500 mt-1">I documenti appariranno qui quando verranno caricati nelle pratiche del cliente</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome Documento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pratica</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Caricamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documentiPratiche.map(documento => (
                  <tr key={`${documento.pratica?.id || documento.id}-${documento.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                        {documento.nomeFile || documento.tipoDocumento}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{documento.pratica?.codicePratica || 'N/A'}</div>
                        <div className="text-xs text-gray-500">ID: {documento.pratica?.id || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {documento.tipoDocumento}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {documento.dataCaricamento ? new Date(documento.dataCaricamento).toLocaleDateString('it-IT') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        documento.stato === 'approvato' ? 'bg-green-100 text-green-800' :
                        documento.stato === 'rifiutato' ? 'bg-red-100 text-red-800' :
                        documento.stato === 'in_revisione' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {documento.stato === 'approvato' ? 'Approvato' :
                         documento.stato === 'rifiutato' ? 'Rifiutato' :
                         documento.stato === 'in_revisione' ? 'In Revisione' :
                         documento.stato || 'Caricato'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                          onClick={() => window.open(`/pratiche/${documento.pratica?.id}`, '_blank')}
                          title="Visualizza pratica"
                          disabled={!documento.pratica?.id}
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Pratica
                        </button>
                        {documento.pratica?.id && (
                          <button
                            className="text-green-600 hover:text-green-800 flex items-center"
                            onClick={() => {
                              // Scarica il documento della pratica con autenticazione
                              downloadAutenticato(
                                `/api/pratica/${documento.pratica.id}/documenti/${documento.id}/download`,
                                documento.nomeFile || documento.nomeOriginale || 'documento'
                              ).catch((e) => toast.error(e.message || 'Errore nel download del documento'));
                            }}
                            title="Scarica documento"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            Scarica
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Caricamento cliente...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-md my-4">
        <p>{error}</p>
        <button
          onClick={() => navigate('/nlt/clienti')}
          className="mt-2 text-blue-600 hover:text-blue-800"
        >
          Torna alla lista
        </button>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Cliente non trovato</p>
        <button
          onClick={() => navigate('/nlt/clienti')}
          className="mt-2 text-blue-600 hover:text-blue-800"
        >
          Torna alla lista
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2">
          <li className="inline-flex items-center">
            <Link to="/nlt" className="text-blue-600 hover:text-blue-800">
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              <Link to="/nlt/clienti" className="ml-1 text-blue-600 hover:text-blue-800">
                Clienti
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              <span className="ml-1 text-gray-500" aria-current="page">
                {client.tipo_cliente === 'privato' 
                  ? `${client.nome} ${client.cognome}`
                  : client.ragione_sociale}
              </span>
            </div>
          </li>
        </ol>
      </nav>
      
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                {client.tipo_cliente === 'privato' 
                  ? `${client.nome} ${client.cognome}`
                  : client.ragione_sociale}
              </h1>
              <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                client.tipo_cliente === 'privato' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
              }`}>
                {client.tipo_cliente === 'privato' ? 'Privato' : 'Aziendale'}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {client.codice ? `Codice: ${client.codice}` : 'Codice non disponibile'}
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/nlt/clienti/${id}/modifica`)}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md flex items-center"
            >
              <PencilIcon className="h-5 w-5 mr-1" />
              Modifica
            </button>
            
            <button
              onClick={handleCreaPratica}
              disabled={creatingPratica}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-medium py-2 px-4 rounded-md flex items-center"
            >
              <DocumentTextIcon className="h-5 w-5 mr-1" />
              {creatingPratica ? 'Creando...' : 'Crea Pratica'}
            </button>
            
            <button
              onClick={() => navigate('/nlt/offerte-vetture-lt')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md flex items-center"
            >
              <DocumentDuplicateIcon className="h-5 w-5 mr-1" />
              Nuovo Preventivo
            </button>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-3 py-2 font-medium text-sm rounded-md ${
              activeTab === 'info'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Informazioni
          </button>
          
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-3 py-2 font-medium text-sm rounded-md ${
              activeTab === 'documents'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Documenti Pratiche
          </button>
        </nav>
      </div>
      
      {/* Tab content */}
      {renderTabContent()}
    </div>
  );
};

export default ClientDetails; 